import { useEffect, useRef, useState } from 'react';
import { getSectionDisplayName, SectionType } from '../utils/sectionClassifier';
import './Waveform.css';

/* Single-letter badges for the marker row.
   Lucide icons disappear at 11 px in narrow segments; letter glyphs read
   instantly and look like a Traktor / DJ deck cue tag. */
const SECTION_GLYPH: Record<SectionType, string> = {
  'mix-in':    'M',
  'intro':     'I',
  'verse':     'V',
  'chorus':    'C',
  'bridge':    'B',
  'breakdown': 'K', // breakdown
  'main-drop': 'D', // drop
  'build':     'U', // up / build
  'outro':     'O',
  'mix-out':   'X',
};

/* Per-section bar tint (RGB) for the waveform canvas.
   Matches the section label colors so the wave reads section-by-section. */
const SECTION_RGB: Record<SectionType, [number, number, number]> = {
  'mix-in':    [161, 161, 170], // platinum/grey
  'intro':     [0, 240, 255],   // cyan
  'verse':     [255, 255, 255], // white
  'chorus':    [255, 16, 240],  // hot pink
  'bridge':    [157, 0, 255],   // electric purple
  'breakdown': [255, 215, 0],   // cyber gold
  'main-drop': [255, 107, 53],  // copper accent
  'build':     [57, 255, 20],   // matrix green
  'outro':     [161, 161, 170],
  'mix-out':   [120, 120, 128],
};

const FALLBACK_RGB: [number, number, number] = [255, 107, 53]; // copper

const VALID_SECTION_TYPES: SectionType[] = [
  'mix-in', 'intro', 'breakdown', 'main-drop', 'bridge',
  'outro', 'mix-out', 'verse', 'chorus', 'build',
];

interface Segment {
  id: string;
  segmentNumber: number;
  startTime: number;
  endTime: number;
  chordProgression?: string[];
  key?: string;
  energy?: number;
  sectionType?: string;
  timestamp?: string;
}

interface WaveformProps {
  audioUrl: string;
  duration: number;
  segments: Segment[];
  currentTime?: number;
  onSeek?: (time: number) => void;
  loopRegion?: { start: number; end: number } | null;
}

interface WaveformBands {
  peaks: number[];
  low: number[];
  mid: number[];
  high: number[];
}

export function Waveform({
  audioUrl,
  duration,
  segments,
  currentTime = 0,
  onSeek,
  loopRegion = null,
}: WaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [bands, setBands] = useState<WaveformBands | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [width, setWidth] = useState(800);

  // Helper function to format time
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Generate waveform data from audio
  useEffect(() => {
    if (!audioUrl || !duration) {
      console.log('Waveform: Missing audioUrl or duration', { audioUrl, duration });
      return;
    }

    const generateWaveform = async () => {
      setIsLoading(true);
      try {
        console.log('Waveform: Starting generation for', audioUrl);
        
        // Create AudioContext (may need user interaction, but try anyway)
        let audioContext: AudioContext;
        try {
          audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        } catch (e) {
          console.error('Waveform: Failed to create AudioContext:', e);
          setIsLoading(false);
          return;
        }

        // Fetch audio file
        const response = await fetch(audioUrl, {
          mode: 'cors',
          credentials: 'omit',
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch audio: ${response.status} ${response.statusText}`);
        }
        
        console.log('Waveform: Fetched audio, decoding...');
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        console.log('Waveform: Decoded audio, generating peaks...', {
          sampleRate: audioBuffer.sampleRate,
          duration: audioBuffer.duration,
          channels: audioBuffer.numberOfChannels,
        });

        // ── Per-bar peaks (overall amplitude — drives bar height) ──
        const samples = audioBuffer.getChannelData(0);
        const samplesPerPixel = Math.floor(samples.length / width);
        const peaks: number[] = [];
        let maxPeak = 0;
        for (let i = 0; i < width; i++) {
          const startIdx = i * samplesPerPixel;
          const endIdx = startIdx + samplesPerPixel;
          let max = 0;
          for (let j = startIdx; j < endIdx && j < samples.length; j++) {
            const abs = Math.abs(samples[j]);
            if (abs > max) max = abs;
          }
          peaks.push(max);
          if (max > maxPeak) maxPeak = max;
        }
        if (maxPeak > 0) {
          for (let i = 0; i < peaks.length; i++) peaks[i] = peaks[i] / maxPeak;
        }

        // ── Spectral bands (Traktor-style coloring: low / mid / high) ──
        // Render the buffer through three biquad filters offline, then sample
        // peak amplitude per pixel from each band. Drives RGB color.
        const renderBand = async (
          buf: AudioBuffer,
          buildFilter: (ctx: OfflineAudioContext) => AudioNode,
        ): Promise<Float32Array> => {
          const offline = new OfflineAudioContext(1, buf.length, buf.sampleRate);
          const src = offline.createBufferSource();
          src.buffer = buf;
          const filter = buildFilter(offline);
          src.connect(filter);
          filter.connect(offline.destination);
          src.start(0);
          const rendered = await offline.startRendering();
          return rendered.getChannelData(0);
        };

        const lowSamples = await renderBand(audioBuffer, (ctx) => {
          const f = ctx.createBiquadFilter();
          f.type = 'lowpass';
          f.frequency.value = 250;
          f.Q.value = 0.707;
          return f;
        });
        const midSamples = await renderBand(audioBuffer, (ctx) => {
          const f = ctx.createBiquadFilter();
          f.type = 'bandpass';
          f.frequency.value = 1000;
          f.Q.value = 0.6;
          return f;
        });
        const highSamples = await renderBand(audioBuffer, (ctx) => {
          const f = ctx.createBiquadFilter();
          f.type = 'highpass';
          f.frequency.value = 2500;
          f.Q.value = 0.707;
          return f;
        });

        const bandPeaks = (data: Float32Array): number[] => {
          const out: number[] = new Array(width);
          let m = 0;
          for (let i = 0; i < width; i++) {
            const s = i * samplesPerPixel;
            const e = s + samplesPerPixel;
            let max = 0;
            for (let j = s; j < e && j < data.length; j++) {
              const abs = Math.abs(data[j]);
              if (abs > max) max = abs;
            }
            out[i] = max;
            if (max > m) m = max;
          }
          if (m > 0) for (let i = 0; i < out.length; i++) out[i] = out[i] / m;
          return out;
        };

        const lowPeaks = bandPeaks(lowSamples);
        const midPeaks = bandPeaks(midSamples);
        const highPeaks = bandPeaks(highSamples);

        console.log('Waveform: Generated bands', {
          peaks: peaks.length,
          maxPeak,
          maxLow: Math.max(...lowPeaks),
          maxMid: Math.max(...midPeaks),
          maxHigh: Math.max(...highPeaks),
        });

        setWaveformData(peaks);
        setBands({ peaks, low: lowPeaks, mid: midPeaks, high: highPeaks });
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to generate waveform:', error);
        console.error('Error details:', {
          audioUrl,
          duration,
          width,
          errorMessage: error instanceof Error ? error.message : String(error),
        });
        setIsLoading(false);
        // Set empty data so canvas still renders
        setWaveformData([]);
      }
    };

    generateWaveform();
  }, [audioUrl, duration, width]);
  
  // Debug: Log when component renders
  useEffect(() => {
    console.log('Waveform component rendered:', {
      hasAudioUrl: !!audioUrl,
      duration,
      width,
      isLoading,
      waveformDataLength: waveformData.length,
      segmentsCount: segments.length,
    });
  }, [audioUrl, duration, width, isLoading, waveformData.length, segments.length]);

  // Update width on resize - ensure we have a valid width
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const newWidth = containerRef.current.offsetWidth;
        if (newWidth > 0) {
          setWidth(newWidth);
        } else {
          // Fallback to a reasonable default if container not ready
          setWidth(800);
        }
      } else {
        setWidth(800);
      }
    };

    // Initial update
    updateWidth();
    
    // Also try after a short delay in case container isn't ready
    const timeout = setTimeout(updateWidth, 100);
    
    window.addEventListener('resize', updateWidth);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  // Draw waveform
  useEffect(() => {
    if (!canvasRef.current || waveformData.length === 0 || width <= 0) {
      console.log('Waveform: Skipping draw', {
        hasCanvas: !!canvasRef.current,
        dataLength: waveformData.length,
        width,
      });
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Waveform: Failed to get 2d context');
      return;
    }

    const height = 140;
    // Ensure canvas has valid dimensions
    if (width <= 0 || height <= 0) {
      console.error('Waveform: Invalid canvas dimensions', { width, height });
      return;
    }
    
    canvas.width = width;
    canvas.height = height;
    
    console.log('Waveform: Drawing canvas', { width, height, peaks: waveformData.length });

    // Clear canvas with KNKT background
    ctx.fillStyle = 'var(--knkt-bg-primary)';
    // Fallback if CSS variable doesn't work
    const bgColor = getComputedStyle(document.documentElement).getPropertyValue('--knkt-bg-primary').trim() || '#0f0f0f';
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, width, height);

    // Draw waveform bars - vertical bars like reference image
    if (waveformData.length > 0) {
      const barWidth = Math.max(1, width / waveformData.length);
      const centerY = height / 2;

      // Find max peak for better visualization
      const maxPeakValue = Math.max(...waveformData);
      console.log('Waveform: Max peak value:', maxPeakValue);

      // ── Traktor-style spectral coloring ──
      // RGB encodes frequency content per bar:
      //   low band  → red/orange
      //   mid band  → yellow/green
      //   high band → cyan/blue
      // Played portion at full opacity, unplayed at ~32% (SoundCloud-style fade).
      const playheadX = duration > 0 ? (currentTime / duration) * width : 0;

      // Subtle horizontal centerline.
      ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
      ctx.fillRect(0, centerY - 0.5, width, 1);

      const lowArr = bands?.low;
      const midArr = bands?.mid;
      const highArr = bands?.high;

      waveformData.forEach((peak, index) => {
        const normalizedPeak = Math.max(0, Math.min(1, peak));
        const amplifiedPeak = normalizedPeak < 0.1 ? normalizedPeak * 5 : normalizedPeak;
        const clampedPeak = Math.min(amplifiedPeak, 1);

        const barHeight = Math.max(2, clampedPeak * height * 0.85);
        const x = index * barWidth;
        const isPlayed = x <= playheadX;

        // Per-band amplitude for this column.
        const lo = lowArr ? lowArr[index] ?? 0 : 0;
        const mi = midArr ? midArr[index] ?? 0 : 0;
        const hi = highArr ? highArr[index] ?? 0 : 0;
        const total = lo + mi + hi || 1;
        // Normalize so the three bands compete for the color budget.
        const lN = lo / total;
        const mN = mi / total;
        const hN = hi / total;

        // Map to Traktor-ish palette:
        //   strong lows → red-orange #ff3a1a
        //   strong mids → amber/yellow #ffaa00
        //   strong highs → cyan #00d9ff
        const r = Math.round(255 * lN + 255 * mN + 0   * hN);
        const g = Math.round( 60 * lN + 170 * mN + 220 * hN);
        const b = Math.round( 30 * lN +   0 * mN + 255 * hN);

        const sat = 0.82 + 0.18 * clampedPeak;
        const alpha = (isPlayed ? sat : sat * 0.35).toFixed(3);

        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
        ctx.fillRect(x, centerY - barHeight / 2, Math.max(1, barWidth - 0.5), barHeight);
      });
      
      console.log('Waveform: Finished drawing', waveformData.length, 'bars');
    } else {
      // Draw placeholder message if no waveform data
      ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.font = '14px var(--knkt-font-family, sans-serif)';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('No waveform data available', width / 2, height / 2);
    }

    // Draw section dividers (subtle vertical lines) - labels are now above canvas
    segments.forEach((segment) => {
      const startX = (segment.startTime / duration) * width;
      const endX = (segment.endTime / duration) * width;
      
      // Draw subtle vertical divider line between sections
      if (endX < width && startX > 0) {
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(endX, 0);
        ctx.lineTo(endX, height);
        ctx.stroke();
      }
    });

    // Draw loop region (Traktor / Ableton style) — translucent green band with IN / OUT markers
    if (loopRegion && duration > 0) {
      const inX = (loopRegion.start / duration) * width;
      const outX = (loopRegion.end / duration) * width;
      const regionW = Math.max(2, outX - inX);

      // Region fill — translucent matrix-green
      ctx.fillStyle = 'rgba(57, 255, 20, 0.10)';
      ctx.fillRect(inX, 0, regionW, height);

      // Top + bottom region rails for that "loop bracket" Traktor look
      ctx.fillStyle = 'rgba(57, 255, 20, 0.6)';
      ctx.fillRect(inX, 0, regionW, 2);
      ctx.fillRect(inX, height - 2, regionW, 2);

      // IN / OUT vertical markers
      const drawMarker = (x: number, label: string, anchor: 'left' | 'right') => {
        ctx.save();
        ctx.shadowBlur = 12;
        ctx.shadowColor = '#39ff14';
        ctx.strokeStyle = '#39ff14';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
        ctx.shadowBlur = 0;

        // Tiny label chip at top
        const labelW = 26;
        const labelH = 14;
        const lx = anchor === 'left' ? x : x - labelW;
        ctx.fillStyle = 'rgba(57, 255, 20, 0.85)';
        ctx.fillRect(lx, 0, labelW, labelH);
        ctx.fillStyle = '#000';
        ctx.font = '700 9px ui-monospace, "SF Mono", Menlo, monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, lx + labelW / 2, labelH / 2 + 1);
        ctx.restore();
      };
      drawMarker(inX, 'IN', 'left');
      drawMarker(outX, 'OUT', 'right');
    }

    // Draw current time indicator (playhead)
    if (currentTime > 0 && duration > 0) {
      const currentX = (currentTime / duration) * width;
      ctx.save();
      ctx.shadowBlur = 12;
      ctx.shadowColor = '#00f0ff';
      ctx.strokeStyle = '#00f0ff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(currentX, 0);
      ctx.lineTo(currentX, height);
      ctx.stroke();
      // Diamond cap at top
      ctx.fillStyle = '#00f0ff';
      ctx.beginPath();
      ctx.moveTo(currentX, 0);
      ctx.lineTo(currentX + 5, 6);
      ctx.lineTo(currentX, 12);
      ctx.lineTo(currentX - 5, 6);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
  }, [waveformData, bands, segments, duration, currentTime, width, loopRegion]);

  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onSeek || !duration) return;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const time = (x / width) * duration;
    onSeek(Math.max(0, Math.min(time, duration)));
  };

  return (
    <div ref={containerRef} className="waveform-container">
      {/* Section labels above waveform — lucide icons keyed by section type */}
      {segments.length > 0 && !isLoading && waveformData.length > 0 && (
        <div className="waveform-labels">
          {segments.map((segment, idx) => {
            const segmentWidth = ((segment.endTime - segment.startTime) / duration) * 100;
            if (segmentWidth < 1) return null;

            let label = '';
            let glyph = '·';
            const sectionType = segment.sectionType as SectionType | undefined;
            if (sectionType && VALID_SECTION_TYPES.includes(sectionType)) {
              label = getSectionDisplayName(sectionType);
              glyph = SECTION_GLYPH[sectionType];
            } else {
              label = segment.sectionType || `Section ${idx + 1}`;
            }

            return (
              <div
                key={segment.id || idx}
                className={`waveform-label-item${sectionType ? ` waveform-label-${sectionType}` : ''}`}
                style={{
                  width: `${segmentWidth}%`,
                  left: `${(segment.startTime / duration) * 100}%`,
                }}
              >
                <div className="waveform-label-name">
                  <span className="waveform-label-badge" aria-hidden="true">{glyph}</span>
                  <span className="waveform-label-text">{label}</span>
                </div>
                <div className="waveform-label-time">
                  {formatTime(segment.startTime)} – {formatTime(segment.endTime)}
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Waveform canvas */}
      {isLoading ? (
        <div className="waveform-loading">
          <div>Generating waveform...</div>
          <div style={{ fontSize: '11px', marginTop: '8px', opacity: 0.6 }}>
            {audioUrl ? `Loading: ${audioUrl.split('/').pop()}` : 'Waiting for audio URL...'}
          </div>
        </div>
      ) : waveformData.length === 0 ? (
        <div className="waveform-loading" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
          <div>Unable to generate waveform</div>
          <div style={{ fontSize: '11px', marginTop: '8px', opacity: 0.6 }}>
            Check browser console for details
          </div>
          {audioUrl && (
            <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.4 }}>
              URL: {audioUrl}
            </div>
          )}
        </div>
      ) : (
        <div style={{ position: 'relative', width: '100%' }}>
          <canvas
            ref={canvasRef}
            className="waveform-canvas"
            onClick={handleClick}
            style={{ 
              cursor: onSeek ? 'pointer' : 'default', 
              display: 'block',
              width: '100%',
              height: '140px',
              backgroundColor: '#0f0f0f',
            }}
          />
          {process.env.NODE_ENV === 'development' && waveformData.length > 0 && (
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', padding: '4px', textAlign: 'center' }}>
              {waveformData.length} peaks rendered | Max peak: {Math.max(...waveformData).toFixed(3)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

